import { ButtonApiErrorWrapper } from 'app/components/shared/ButtonApiErrorWrapper'
import { Button } from 'app/components/ui/Button'
import { Modal } from 'app/components/ui/Modal'
import { Text } from 'app/components/ui/Text'
import { useSetStatusSponsorRequest } from 'app/queries/sponsorRequests'
import { SponsorRequest, SponsorRequestStatus } from 'EstraSponsor'
import React from 'react'
import styled from 'styled-components'

import { sponsorRequestHasChanges } from './utils'

type Props = {
  sponsorRequest: SponsorRequest
  onCancel: () => void
  onConfirm: () => void
}

export const SponsorRequestEvaluateChangesModal = ({
  sponsorRequest,
  onCancel,
  onConfirm,
}: WithChildren<Props>): JSX.Element => {
  const {
    mutate: setStatusSponsorRequest,
    isLoading,
    error,
  } = useSetStatusSponsorRequest()

  const setStatusSponsorRequestHandler = (
    status: Extract<SponsorRequestStatus, 'canceled' | 'approved'>
  ) =>
    setStatusSponsorRequest(
      {
        sponsor_request_id: sponsorRequest.id.toString(),
        status: status,
      },
      {
        onSuccess: () => {
          onConfirm()
        },
      }
    )

  const sponsorRequestChanges = sponsorRequest?.changes?.changes

  const hasChanges = sponsorRequestHasChanges(sponsorRequest)

  return (
    <Modal
      onCloseRequest={() => onCancel()}
      hasOuterScroll
      title={`Conferma l'operazione`}
    >
      <ModalWrapper>
        {sponsorRequestChanges && hasChanges ? (
          <>
            <SponsorRequestChangeItem
              title='Contributo Economico'
              oldValue={
                sponsorRequestChanges.financial_contribution?.old
                  ? `${parseInt(
                      sponsorRequestChanges.financial_contribution.old
                    ).toString()} €` // To remove digits
                  : undefined
              }
              newValue={
                sponsorRequestChanges.financial_contribution?.new
                  ? `${parseInt(
                      sponsorRequestChanges.financial_contribution.new
                    ).toString()} €` // To remove digits
                  : undefined
              }
            />
            <SponsorRequestChangeItem
              title='Contributo Tecnico'
              oldValue={sponsorRequestChanges.technical_contribution?.old}
              newValue={sponsorRequestChanges.technical_contribution?.new}
            />
            <SponsorRequestChangeItem
              title='Benefit per lo sponsor'
              oldValue={sponsorRequestChanges.benefits?.old}
              newValue={sponsorRequestChanges.benefits?.new}
            />
            <SponsorRequestChangeItem
              title='Visibilità e comunicazione del brand'
              oldValue={sponsorRequestChanges.logo_visibility?.old}
              newValue={sponsorRequestChanges.logo_visibility?.new}
            />
          </>
        ) : (
          <NoChangesWrapper>
            <Text variant='s20'>
              Non è stata apportata nessuna modifica alla tua richiesta.
            </Text>
          </NoChangesWrapper>
        )}
      </ModalWrapper>
      <ButtonApiErrorWrapper isError={!!error} error={error}>
        <ButtonsWrapper>
          {hasChanges && (
            <Button
              onClick={() => {
                setStatusSponsorRequestHandler('canceled')
              }}
              disabled={isLoading}
              variant='grey'
            >
              Rifiuta
            </Button>
          )}
          <Button
            onClick={() => {
              setStatusSponsorRequestHandler('approved')
            }}
            disabled={isLoading}
          >
            {hasChanges ? 'Approva' : 'Continua'}
          </Button>
        </ButtonsWrapper>
      </ButtonApiErrorWrapper>
    </Modal>
  )
}

type SponsorRequestChangeItemProp = {
  title: string
  oldValue?: string | null
  newValue?: string | null
}

const SponsorRequestChangeItem = ({
  title,
  oldValue,
  newValue,
}: SponsorRequestChangeItemProp): JSX.Element => {
  return !!(oldValue || newValue) ? (
    <ListItem>
      <Text variant='s20' weight='bold'>
        {title}:
      </Text>
      <Text variant='s18'>
        Valore originale:{'\n'} <OldValueText>{oldValue || ''}</OldValueText>
      </Text>
      <Text variant='s18'>
        Valore modificato:{'\n'}
        <NewValueText>{newValue || ''}</NewValueText>
      </Text>
    </ListItem>
  ) : (
    <></>
  )
}

const ModalWrapper = styled.div`
  margin: 0 0 3rem 0;
`

const ListItem = styled.div`
  display: grid;
  row-gap: 0.5rem;

  border-bottom: 1px solid ${({ theme }) => theme.color.neutri.grigioBg};
  padding: 1rem 0rem;

  @media (min-width: ${({ theme }) => theme.bp.tablet}) {
    grid-template-columns: '1fr';
    column-gap: 2rem;
  }
`

const OldValueText = styled.span`
  color: ${({ theme }) => theme.color.primari.arancione};
  opacity: 70%;
`

const NewValueText = styled.span`
  color: ${({ theme }) => theme.color.primari.grigioEstra};
`

const NoChangesWrapper = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
`

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  button:first-child {
    margin: 0 0 1rem 0;
  }

  @media (min-width: ${({ theme }) => theme.bp.tablet}) {
    flex-direction: row;
    justify-content: flex-end;

    button:first-child {
      margin: 0 1rem 0 0;
    }
  }
`
